/**
 * Query-key factory for the BI domain (dashboards / widgets / queries).
 * Mirrors the `*Keys` factory convention used across `query/keys/`.
 *
 * NOTE: the `["bi"]` root is deliberately NOT in the persister allow-list in
 * `query/providers/query-provider.tsx` — BI is genuine network data, refetched
 * on demand, never restored from a previous session's IndexedDB cache.
 */
export const biKeys = {
  all: ["bi"] as const,

  dashboards: () => [...biKeys.all, "dashboards"] as const,
  dashboardList: () => [...biKeys.dashboards(), "list"] as const,
  dashboardDetails: () => [...biKeys.dashboards(), "detail"] as const,
  dashboardDetail: (id: string) => [...biKeys.dashboardDetails(), id] as const,
  // Mutation key for the layout-save flow. The live `/changes` poll
  // (`useDashboardLiveWidgets`) is a raw `setInterval`, NOT a TanStack query, so
  // `cancelQueries` can't stop it — instead it checks `isMutating` on this key
  // and skips its optimistic-clobbering cache write while a save is in flight.
  dashboardSaveLayout: (id: string) =>
    [...biKeys.dashboardDetail(id), "save-layout"] as const,
  // Mutation key for the widget-rename flow (STK-734). Like the layout save, the
  // optimistic rename writes straight into the dashboard detail cache, and the
  // live `/changes` poll (`useDashboardLiveWidgets`) checks `isMutating` on this
  // key to skip its own clobbering cache write while a rename is in flight. Keyed
  // by dashboard id so a rename on one board never fences another's poll.
  dashboardRenameWidget: (id: string) =>
    [...biKeys.dashboardDetail(id), "rename-widget"] as const,
  // Mutation key for the widget-delete flow. Like the layout save and rename,
  // the optimistic delete drops the widget straight from the dashboard detail
  // cache, and the live `/changes` poll (`useDashboardLiveWidgets`) checks
  // `isMutating` on this key to skip its own clobbering cache write while a
  // delete is in flight — so a `/changes` poll can't resurrect the widget
  // mid-flight. Keyed by dashboard id so a delete on one board never fences
  // another's poll.
  dashboardDeleteWidget: (id: string) =>
    [...biKeys.dashboardDetail(id), "delete-widget"] as const,

  widgets: () => [...biKeys.all, "widgets"] as const,
  widgetList: () => [...biKeys.widgets(), "list"] as const,
  widgetDetail: (id: string) => [...biKeys.widgets(), "detail", id] as const,

  queries: () => [...biKeys.all, "queries"] as const,
  queryList: () => [...biKeys.queries(), "list"] as const,
  queryDetail: (id: string) => [...biKeys.queries(), "detail", id] as const,
} as const;
